import useQueryFilters, { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import type { QueryParamType } from '@app/src/hooks/queryState'
import type { FilterGroupQueryString, Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResetPage } from '@app/src/types/filter'
import React, { createContext, useContext, useMemo, useState } from 'react'

export const DEFAULT_FILTER_NAME = 'filters'

export interface FilterInterface {
  ['name']: string
  ['operator']: Operators
  [key: string]: string | Array<string>
}

export interface FiltersContextProps {
  filters: Record<string, FilterInterface | FilterInterface[]>
  setQueryFilter: (
    name: string,
    value: FilterInterface['value'],
    operator: Operators,
    uniqueId?: string,
    replace?: boolean,
    intersect?: boolean,
  ) => void
  setQueryFilters: (value: QueryParamType, replace?: boolean | undefined) => void
  setQueryFilterOperator: (name: string, operator: Operators, uniqueId?: string) => void
  allowedFilters: Array<string>
  clearQueryFilter: () => void
  apiFilters: FilterGroupQueryString[]
  visibleFilters: Array<string>
  addQuickFilter: (name: string) => void
  inDrawerFiltersCount: number
}

export interface FiltersChildrenRenderProps {
  filters: FiltersContextProps['filters']
  setQueryFilter: FiltersContextProps['setQueryFilter']
  clearQueryFilter: FiltersContextProps['clearQueryFilter']
  inDrawerFiltersCount: number
  isFiltered: boolean
}

export interface FiltersProps {
  allowedFilters: FiltersContextProps['allowedFilters']
  resetPage?: ResetPage
  // The filters to send with the facet requests, when the URL parameters alone are not what the API
  // should receive. Providers pass their own so the facet counts are built on the same filters as the
  // table (see useProviderApiQueryFilters); everything else takes the URL parameters as they are.
  apiFilters?: FilterGroupQueryString[]
  children: ({ filters, setQueryFilter, clearQueryFilter, isFiltered }: FiltersChildrenRenderProps) => JSX.Element
}

const FiltersContext = createContext<FiltersContextProps>({
  filters: {},
  setQueryFilter: () => undefined,
  setQueryFilters: () => undefined,
  setQueryFilterOperator: () => undefined,
  clearQueryFilter: () => undefined,
  allowedFilters: [],
  apiFilters: [],
  visibleFilters: [],
  addQuickFilter: () => undefined,
  inDrawerFiltersCount: 0,
})

export const useFiltersContext = (): FiltersContextProps => {
  return useContext(FiltersContext)
}

const Filters: React.FC<FiltersProps> = ({ allowedFilters, resetPage, apiFilters: apiFiltersOverride, children }) => {
  const { filters, setQueryFilter, clearQueryFilter, setQueryFilterOperator, setQueryFilters } = useQueryFilters(
    allowedFilters,
    resetPage,
  )
  const urlApiFilters = useGetApiQueryFilters(allowedFilters)
  const apiFilters = apiFiltersOverride ?? urlApiFilters
  const [quickFilters, setQuickFilters] = useState<Array<string>>([])

  const addQuickFilter = (name: string) => {
    setQuickFilters(prev => Array.from(new Set([...prev, name])))
  }
  const inDrawerFiltersCount = useMemo(
    () =>
      Object.values(filters)
        .flat()
        .filter(apiFilter => !quickFilters.includes(apiFilter.name)).length,
    [quickFilters, filters],
  )

  const isFiltered = Object.keys(filters).length > 0

  return (
    <FiltersContext.Provider
      value={{
        filters,
        allowedFilters,
        setQueryFilter,
        setQueryFilters,
        setQueryFilterOperator,
        clearQueryFilter,
        apiFilters,
        visibleFilters: quickFilters,
        addQuickFilter,
        inDrawerFiltersCount,
      }}
    >
      <>
        {children({
          filters,
          setQueryFilter,
          clearQueryFilter,
          inDrawerFiltersCount,
          isFiltered,
        })}
      </>
    </FiltersContext.Provider>
  )
}

export default Filters
